import { inject, Injectable } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { AppInsightsFeatureType } from '../../models/app-insights-feature-type';
import { closeIdleWarningDialog, openIdleWarningDialog } from '../../store/actions/app.actions';
import { AppInsightsService } from '../app-insights/app-insights.service';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  private idle = inject(Idle);
  private keepalive = inject(Keepalive);
  private authService = inject(AuthService);
  private appInsightsService = inject(AppInsightsService);
  private store = inject(Store);
  private broadcastChannel = new BroadcastChannel('portal_idle_broadcast');

  private isUserIdle = false;
  private tabId: string | null = null;
  private heartbeatInterval: number | undefined;
  private tokenCheckInterval: number | undefined;
  private logoutCountdownSubject = new Subject<number>();
  private hasStarted = false;

  private readonly TAB_LEADER_KEY = 'portal_leader';
  private readonly TAB_LEADER_HEARTBEAT_KEY = 'portal_leader_heartbeat';
  private readonly LEADER_TIMEOUT = 5000; // leader times out after 5 seconds of no heartbeat
  private readonly TIMEOUT_INTERVAL = 2 * 60; // log out after 2 minutes of inactivity after warning
  private readonly IDLE_WARNING_INTERVAL = 3 * 60; // show idle modal after 3 minutes of inactivity

  // Broadcast message keys
  private readonly LOGOUT = 'logout';
  private readonly IDLE_RESET = 'idle_reset';
  private readonly LEADER_REMOVED = 'leader_removed';

  logoutCountdown$ = this.logoutCountdownSubject.asObservable();

  constructor() {
    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      switch (event.data) {
        case this.LOGOUT:
          this.authService.clearCache();
          break;
        case this.IDLE_RESET:
          this.onResetBroadcast();
          break;
        case this.LEADER_REMOVED:
          this.tryBecomeLeader();
          break;
        default:
          break;
      }
    };
  }

  start() {
    if (this.hasStarted) {
      return;
    }

    this.hasStarted = true;
    this.tryBecomeLeader();
    this.manageToken();
    this.initializeIdle();
    window.addEventListener('beforeunload', () => this.destroy());
  }

  initiateReset(): void {
    this.broadcastChannel.postMessage(this.IDLE_RESET);
    this.authService.refreshToken();
    this.reset();
    this.appInsightsService.trackEvent('Auth.IdleWarning.Dismissed', {
      feature: AppInsightsFeatureType.AUTH,
    });
  }

  private onResetBroadcast(): void {
    this.store.dispatch(closeIdleWarningDialog());
    this.reset();
  }

  private reset(): void {
    this.isUserIdle = false;
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
  }

  private initiateLogout(): void {
    if (this.tryBecomeLeader()) {
      this.broadcastChannel.postMessage(this.LOGOUT);
      this.appInsightsService.trackEvent('Auth.IdleTimeout.Complete', { feature: AppInsightsFeatureType.AUTH });
      this.authService.logout();
    }
  }

  private initializeIdle(): void {
    this.idle.setIdle(this.IDLE_WARNING_INTERVAL);
    this.idle.setTimeout(this.TIMEOUT_INTERVAL);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.keepalive.interval(15);

    this.idle.onIdleStart.subscribe(() => {
      if (!this.isUserIdle) {
        this.authService.refreshToken();
        this.idle.clearInterrupts();
        this.store.dispatch(openIdleWarningDialog());
        this.isUserIdle = true;
        this.appInsightsService.trackEvent('Auth.IdleWarning.Started', {
          feature: AppInsightsFeatureType.AUTH,
        });
      }
    });
    this.idle.onTimeoutWarning.subscribe(countdown => this.logoutCountdownSubject.next(countdown));
    this.idle.onTimeout.subscribe(() => this.initiateLogout());

    this.idle.watch();
  }

  private tryBecomeLeader(): boolean {
    if (!this.tabId) {
      this.tabId = crypto.randomUUID();
    }
    const tabId = this.tabId;
    let currentLeader = localStorage.getItem(this.TAB_LEADER_KEY);
    const lastHeartbeat = Number(localStorage.getItem(this.TAB_LEADER_HEARTBEAT_KEY));
    const now = Date.now();

    if (!currentLeader || !lastHeartbeat || now - lastHeartbeat > this.LEADER_TIMEOUT) {
      currentLeader = tabId;
      localStorage.setItem(this.TAB_LEADER_KEY, tabId);
      localStorage.setItem(this.TAB_LEADER_HEARTBEAT_KEY, now.toString());
      this.startHeartbeat();
    }

    return tabId.toString() === currentLeader;
  }

  private destroy(): void {
    if (localStorage.getItem(this.TAB_LEADER_KEY) === this.tabId) {
      localStorage.removeItem(this.TAB_LEADER_KEY);
      localStorage.removeItem(this.TAB_LEADER_HEARTBEAT_KEY);
      this.broadcastChannel.postMessage(this.LEADER_REMOVED);
    }
    window.clearInterval(this.heartbeatInterval);
    window.clearInterval(this.tokenCheckInterval);
    // Nothing is subscribed or scheduled any more, so let a later start() rebuild it. Only reachable
    // via the beforeunload listener today, but the guard in start() is only honest while hasStarted
    // means "the intervals and idle subscriptions are live".
    this.hasStarted = false;
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = window.setInterval(() => {
      localStorage.setItem(this.TAB_LEADER_HEARTBEAT_KEY, Date.now().toString());
    }, 2000);
  }

  private manageToken(): void {
    this.tokenCheckInterval = window.setInterval(() => {
      const secondsToTokenExpiration =
        Math.floor(this.authService.tokenExpiration.getTime() - new Date().getTime()) / 1000;
      const tokenRefreshRequired = !this.isUserIdle && secondsToTokenExpiration <= 90;
      if (tokenRefreshRequired) {
        this.authService.refreshToken();
      }
    }, 60000);
  }
}
